// Fuseau horaire de l'application, côté navigateur (proposition PROPOSITION_FUSEAU_HORAIRE.md, lot T1).
// Règle : le serveur stocke des instants en UTC « AAAA-MM-JJThh:mm » (ou des dates nues) ; on convertit ici, à la saisie et à l'affichage, dans le
// fuseau de la personne (Profil), à défaut celui de l'entreprise (Paramètres › Général), à défaut Dakar. L'heure d'été est la règle du fuseau
// pour la DATE concernée. Les fonctions prennent le fuseau en argument : testables sans dépendre de la machine.
export const DEFAULT_ZONE = "Africa/Dakar";

// La liste fermée du serveur (`backend/timekit.py::ZONES`) : un test du backend compare les deux.
export const ZONES = ["Africa/Dakar", "Africa/Abidjan", "Africa/Accra", "Africa/Algiers", "Africa/Bamako", "Africa/Banjul", "Africa/Brazzaville", "Africa/Cairo", "Africa/Casablanca", "Africa/Conakry", "Africa/Douala", "Africa/Freetown", "Africa/Johannesburg", "Africa/Kinshasa", "Africa/Lagos", "Africa/Libreville", "Africa/Lome", "Africa/Monrovia", "Africa/Nairobi", "Africa/Niamey", "Africa/Nouakchott", "Africa/Ouagadougou", "Africa/Tunis", "Europe/Paris", "Europe/Amsterdam", "Europe/Berlin", "Europe/Brussels", "Europe/Lisbon", "Europe/London", "Europe/Madrid", "Europe/Rome", "Europe/Zurich", "America/Montreal", "America/New_York", "America/Chicago", "America/Los_Angeles", "America/Sao_Paulo", "Asia/Beirut", "Asia/Dubai", "Asia/Istanbul", "Asia/Kolkata", "Asia/Shanghai", "Asia/Singapore", "UTC"];

const MINUTE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

export const isZone = (zone) => typeof zone === "string" && ZONES.includes(zone);

// Fuseau de l'appareil, tel que le navigateur le dit (jamais appliqué sans demander : voir `deviceZoneSuggestion`).
export function deviceZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

// Fuseau effectif d'une personne : celui que le serveur calcule (`effective_timezone`), sinon son choix, sinon l'entreprise, sinon Dakar.
export function userZone(user) {
  for (const zone of [user?.effective_timezone, user?.timezone, user?.company_timezone]) if (isZone(zone)) return zone;
  return DEFAULT_ZONE;
}

function partsIn(date, zone) {
  const out = {};
  for (const p of new Intl.DateTimeFormat("en-CA", { timeZone: zone, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }).formatToParts(date)) out[p.type] = p.value;
  return out;
}

// « AAAA-MM-JJThh:mm » en UTC -> la même heure lue dans `zone` (même format, pour un champ datetime-local ou un affichage).
export function utcToLocal(value, zone) {
  const m = MINUTE.exec(String(value || ""));
  if (!m) return value || "";
  const p = partsIn(new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:00Z`), zone || DEFAULT_ZONE);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

// « AAAA-MM-JJThh:mm » lu dans `zone` -> le même instant en UTC. Une heure qui n'existe pas (passage à l'heure d'été) ou qui existe deux fois
// (retour à l'heure d'hiver) prend la première lecture, comme le serveur.
export function localToUtc(value, zone) {
  const m = MINUTE.exec(String(value || ""));
  if (!m) return value || "";
  const asUtc = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
  const z = zone || DEFAULT_ZONE;
  const DAY = 86400000;
  const offsetAt = (t) => { const p = partsIn(new Date(t), z); return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute) - Math.floor(t / 60000) * 60000; };
  const reads = (t) => { const p = partsIn(new Date(t), z); return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}` === `${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}`; };
  // Le décalage d'AVANT le changement d'heure (la veille) donne la première lecture d'une heure qui existe deux fois et, pour une heure qui
  // n'existe pas, la lecture que fait le serveur ; celui d'APRÈS ne sert que si la première ne retombe pas sur l'heure demandée.
  const before = asUtc - offsetAt(asUtc - DAY);
  if (reads(before)) return new Date(before).toISOString().slice(0, 16);
  const after = asUtc - offsetAt(asUtc + DAY);
  return new Date(reads(after) ? after : before).toISOString().slice(0, 16);
}

// « Paris », « New York », « Dakar » : le nom lisible d'un fuseau, pour dire dans quelle heure on lit.
export function zoneName(zone) {
  if (zone === "UTC") return "UTC";
  return String(zone || DEFAULT_ZONE).split("/").pop().replace(/_/g, " ");
}

// Le libellé d'un fuseau avec son décalage du moment : « Paris (UTC+1) ».
export function zoneLabel(zone, at = new Date()) {
  const p = partsIn(at, zone || DEFAULT_ZONE);
  const offset = (Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute) - Math.floor(at.getTime() / 60000) * 60000) / 3600000;
  const sign = offset < 0 ? "−" : "+";
  const abs = Math.abs(offset);
  const hours = Math.trunc(abs);
  const minutes = Math.round((abs - hours) * 60);
  return `${zoneName(zone)} (UTC${sign}${hours}${minutes ? `:${String(minutes).padStart(2, "0")}` : ""})`;
}

// L'appareil dit un autre fuseau que celui qu'utilise la personne ? Une SUGGESTION (jamais appliquée seule) : un déplacement ou un poste mal réglé
// ne doit pas changer des heures sans prévenir. Seulement pour un fuseau de la liste fermée.
export function deviceZoneSuggestion(user, device = deviceZone()) {
  return isZone(device) && device !== userZone(user) ? device : null;
}

// Fuseau « en cours » de la personne connectée : posé par `AuthContext` à chaque changement d'utilisateur, lu par les affichages qui n'ont pas de
// fuseau en argument (listes, fiches). Les fonctions ci-dessus prennent toujours le fuseau explicitement.
let activeZone = DEFAULT_ZONE;
export const setActiveZone = (zone) => { activeZone = isZone(zone) ? zone : DEFAULT_ZONE; };
export const getActiveZone = () => activeZone;

// Fuseau de l'ENTREPRISE (Paramètres › Général), posé comme le précédent : il préremplit le fuseau d'une nouvelle mission et fixe la frontière
// de jour du serveur (« en retard »).
let companyZone = DEFAULT_ZONE;
export const setCompanyZone = (zone) => { companyZone = isZone(zone) ? zone : DEFAULT_ZONE; };
export const getCompanyZone = () => companyZone;

// --- Instants ISO (« 2026-11-01T10:30:00.000Z ») <-> champ datetime-local, lus dans le fuseau de la personne --------------------------------
// Les formulaires d'heure (incident, échange de ticket, compte rendu de pilote…) passent tous par ici : la valeur saisie est une heure du fuseau
// de la personne, jamais celle de l'appareil.
const pad2 = (n) => String(n).padStart(2, "0");

export function isoToInput(iso, zone = activeZone) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return utcToLocal(d.toISOString().slice(0, 16), zone);
}

export function inputToIso(value, zone = activeZone) {
  if (!value || !MINUTE.test(String(value))) return null;
  const utc = localToUtc(String(value).slice(0, 16), zone);
  const d = new Date(`${utc}:00Z`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export const nowInput = (zone = activeZone, now = new Date()) => isoToInput(now.toISOString(), zone);

const FORMAT = { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" };
export function formatInstant(iso, zone = activeZone) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("fr-FR", { ...FORMAT, timeZone: zone });
}

// Le jour civil (« AAAA-MM-JJ ») d'un instant, dans le fuseau de l'ENTREPRISE : la même frontière que le serveur pour « en retard ».
export function companyDay(now = new Date()) {
  return utcToLocal(now.toISOString().slice(0, 16), companyZone).slice(0, 10);
}
